import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { viewClick } from "./ActionsRenderer.utils";
import styles from "./ActionsRenderer.module.css";
import { ActionsRendererProps } from "./ActionsRenderer.types";
import { getFromLS } from "../../../../../utils/storage.js";

/**
 * Renders action buttons for viewing and downloading an engagement letter PDF.
 *
 * This component shows two buttons with tooltips:
 * - "View" button opens the engagement letter in a preview modal.
 * - "Download" button triggers a download of the engagement letter.
 *
 * @component
 * @param {ActionsRendererProps} props - Component props
 * @param {(url: string) => void} props.setPdfFileUrl - Function to update the URL of the PDF preview
 * @param {(visible: boolean) => void} props.setTemplatePreviewModal - Function to toggle visibility of the preview modal
 *
 * @returns {JSX.Element} The rendered action buttons with tooltips
 */
const ActionsRenderer: React.FC<ActionsRendererProps> = ({
  setPdfFileUrl,
  setTemplatePreviewModal,
  setIsSummaryModalOpen,
  setELId,
  setELFileName,
  transporter,
  data
}) => {
  const availableFeatures = getFromLS("active-features")
  const showELSummary = Array.isArray(availableFeatures)
    ? availableFeatures.some((str: string) => str.includes("EL_SET_SUMMARY"))
    : false;

  const summaryBtnClick = () => {
    setELId(data.ProcessedTemplateId);
    setELFileName(data.LetterFileName);
    setIsSummaryModalOpen(true);
  }

  const reviewConflictClick = () => {
    if (!transporter) return;
    transporter("coi_conflict_check", {
      coi_conflict_check_option: {
        clientMasterId: data.ClientMasterId,
        processedTemplateId: data.ProcessedTemplateId,
      },
    });
  };

  return (
    <div className={styles.actionButtonWrapper}>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="button-tooltip">View document</Tooltip>}
      >
        <span className={styles.tooltipWrapper}>
          <Button
            className={styles.actionButton}
            onClick={() =>
              viewClick({
                letterFileName: data.LetterFileName,
                setPdfFileUrl,
                setTemplatePreviewModal,
              })
            }
          >
            <Icon.Eye />
          </Button>
        </span>
      </OverlayTrigger>
      
      {showELSummary &&
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={
            <Tooltip id="button-tooltip">
              Generate summary and key insights
            </Tooltip>
          }
        >
          <div className={styles.tooltipWrapper} style={{
            cursor: "pointer"
          }}>
            <Button
              className={styles.actionButton}
              style={{
                pointerEvents: "all"
              }}
              onClick={summaryBtnClick}>
              <Icon.Magic />
            </Button>
          </div>
        </OverlayTrigger>
      }

      {Boolean(data.COIApprovalStatus) &&
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="button-tooltip">Review conflict check results</Tooltip>}
        >
          <span className={styles.tooltipWrapper}>
            <Button
              className={styles.conflictButton}
              onClick={reviewConflictClick}
            >
              <Icon.ShieldExclamation />
            </Button>
          </span>
        </OverlayTrigger>
      }
    </div>
  );
};

export default ActionsRenderer;
